import React from 'react';
import { Theme, Grid } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { OrderProduct, Shopper, SetProduct, DinCollectionMethod, PartialOrder } from 'common/types';
import ProductDinOptionsInput from '../../ProductDinOptionsInput';
import { useTranslation } from 'services/localization/useTranslation';
import { getCalculatedDinValue } from './utils';

interface OwnProps {
	productToUpdate: OrderProduct;
	product: OrderProduct | SetProduct;
	productId: string;
	setProduct: boolean;
	setIndex?: number;
	shopper: Shopper;
	dinCollectionMethod: DinCollectionMethod;
	showRentalViewFormErrors: boolean;
	updateActiveShopperId: (shopperId: string) => void;
	updateActivePartialOrder: (partialOrder: PartialOrder) => void;
}

type Props = OwnProps & WithStyles<typeof styles>;

export const DinAndSoleLengthContainer = (props: Props) => {
	const { classes, product, shopper, dinCollectionMethod, productId, setProduct, setIndex } = props;
	const { t } = useTranslation();
	const dinError = getCalculatedDinValue({
		userProperties: shopper.userProperties,
		soleLength: product.dinValues?.soleLength,
		dinVariation: product.dinValues?.dinVariation,
		t,
	}).error;

	const isProductReturned = () => {
		if ((product as SetProduct).returnedDate || (product as OrderProduct).endDateReturned) {
			return true;
		}
		return false;
	};

	const getDinHelperText = (opts: {
		dinCollectionMethod: DinCollectionMethod;
		productDinValues: OrderProduct['dinValues'];
	}) => {
		const { dinCollectionMethod, productDinValues } = opts;
		const calculatedDinValue = productDinValues?.calculatedDin ?? null;
		const customDinValue = productDinValues?.customDin;
		const showCustomDinInfo =
			dinCollectionMethod === 'automatic' &&
			customDinValue &&
			Number(customDinValue) !== Number(calculatedDinValue);
		const customDinText = !showCustomDinInfo
			? null
			: 'Custom DIN' +
			  (calculatedDinValue
					? ` (${t('din.calculated', 'Calculated')}: ${calculatedDinValue})`
					: '');
		const showDinError = dinCollectionMethod === 'automatic' && Boolean(dinError);
		const dinVariation = product.dinValues?.dinVariation ?? null;
		const dinVariationText = !dinVariation
			? null
			: `${t('din.dinVariation', 'DIN variation')}: ${
					Number(dinVariation) && Number(dinVariation) > 0 ? '+' : ''
			  }${dinVariation}`;
		const helperText =
			(showCustomDinInfo && customDinText) ||
			(showDinError && dinError) ||
			(dinVariation && dinVariationText) ||
			'';
		return helperText;
	};

	const renderProductDinFields = () => {
		let dinComponent = null;
		let bootSoleComponent = null;
		if (
			(product.collectDin && dinCollectionMethod === 'automatic') ||
			(product.collectBootSoleLength && dinCollectionMethod === 'manual')
		) {
			const soleLengthValue = product.dinValues && product.dinValues.soleLength;
			bootSoleComponent = (
				<ProductDinOptionsInput
					product={product}
					shopper={shopper}
					productId={productId}
					setIndex={setProduct && setIndex != null ? setIndex : null}
					name={'soleLength'}
					type="number"
					value={soleLengthValue || ''}
					disabled={isProductReturned()}
					label={t('din.soleLength', 'Sole length')}
					dinCollectionMethod={dinCollectionMethod}
				/>
			);
		}
		if (product.collectDin) {
			const calculatedDinValue: null | number =
				(product.dinValues && product.dinValues.calculatedDin) || null;
			const customDinValue = product.dinValues && product.dinValues.customDin;
			const helperText = getDinHelperText({
				dinCollectionMethod,
				productDinValues: product.dinValues,
			});
			dinComponent = (
				<ProductDinOptionsInput
					product={product}
					shopper={shopper}
					productId={props.productId}
					setIndex={props.setProduct && props.setIndex != null ? props.setIndex : null}
					name="customDin"
					type="number"
					value={
						customDinValue || customDinValue === '' ? customDinValue : calculatedDinValue || ''
					}
					disabled={isProductReturned()}
					helperText={helperText}
					label={'DIN'}
					error={props.showRentalViewFormErrors}
					dinCollectionMethod={dinCollectionMethod}
				/>
			);
		}
		return (
			<Grid container spacing={1}>
				{bootSoleComponent && (
					<Grid item xs>
						{bootSoleComponent}
					</Grid>
				)}
				{dinComponent && (
					<Grid item xs>
						{dinComponent}
					</Grid>
				)}
			</Grid>
		);
	};

	return product.collectDin || product.collectBootSoleLength ? (
		<Grid
			item
			xs={12}
			sm={product.customValuesToCollect && product.customValuesToCollect.length ? 6 : 12}
			lg={6}
			onClick={(e) => {
				e.stopPropagation();
				props.updateActiveShopperId(shopper.id);
			}}
			className={classes.order2smDown}
		>
			{!(props.product as SetProduct).removedFromParent ? renderProductDinFields() : ''}
		</Grid>
	) : null;
};

const styles = (theme: Theme) =>
	createStyles({
		order2smDown: {
			[theme.breakpoints.down('lg')]: {
				order: 2,
			},
		},
	});

export default withStyles(styles)(DinAndSoleLengthContainer);
